// GameSearchDropdown.jsx - Unified inline games search dropdown component
import React, { useState, useRef, useEffect } from 'react';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import '../features/ModalComponents.css';

/**
 * GameSearchDropdown - Unified inline games search dropdown component
 *
 * A reusable, presentational component that provides consistent game search
 * functionality across the application with configurable display options.
 *
 * @typedef {Object} Game
 * @property {number|string} id - Unique game identifier
 * @property {string} name - Game name
 * @property {number} [year_published] - Publication year
 * @property {string} [thumbnail] - Thumbnail image URL
 * @property {string} [image] - Full image URL
 * @property {number} [min_players] - Minimum player count
 * @property {number} [max_players] - Maximum player count
 * @property {number} [min_playtime] - Minimum playtime in minutes
 * @property {number} [max_playtime] - Maximum playtime in minutes
 *
 * @typedef {Object} GameSearchDropdownProps
 * @property {string} searchQuery - Current search query
 * @property {(query: string) => void} onSearchChange - Handler for input changes
 * @property {boolean} isVisible - Whether dropdown is visible
 * @property {(visible: boolean) => void} onVisibilityChange - Handler for visibility changes
 * @property {Game[]} searchResults - Array of search results
 * @property {boolean} isLoading - Loading state
 * @property {(game: Game) => void} onGameSelect - Game selection handler
 * @property {string} [placeholder="Search for games..."] - Input placeholder text
 * @property {boolean} [showImages=false] - Whether to show game images
 * @property {boolean} [autoFocus=false] - Whether to auto-focus the input
 * @property {React.CSSProperties} [containerStyle] - Container style overrides
 * @property {React.CSSProperties} [inputStyle] - Input style overrides
 * @property {React.CSSProperties} [dropdownStyle] - Dropdown style overrides
 * @property {boolean} [showNoResultsMessage=true] - Whether to show "no results" message
 * @property {string} [noResultsMessage="No games found"] - Custom no results message
 * @property {string} [loadingMessage="Searching..."] - Custom loading message
 * @property {React.RefObject} [containerRef] - External container ref
 * @property {React.RefObject} [inputRef] - External input ref
 * @property {React.ReactNode} [trailingSlot] - Optional control rendered inside the field (e.g. clear), anchored right
 *
 * @param {GameSearchDropdownProps} props
 * @returns {React.ReactElement}
 */

const GameSearchDropdown = ({
  searchQuery,
  onSearchChange,
  isVisible,
  onVisibilityChange,
  searchResults,
  isLoading,
  onGameSelect,
  placeholder = "Search for games...",
  showImages = false,
  autoFocus = false,
  containerStyle,
  inputStyle,
  dropdownStyle,
  showNoResultsMessage = true,
  noResultsMessage = "No games found",
  loadingMessage = "Searching...",
  containerRef,
  inputRef,
  trailingSlot = null,
}) => {
  const { trackClick } = useAnalytics();
  const [isFocused, setIsFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const internalContainerRef = useRef(null);
  const internalInputRef = useRef(null);
  const internalDropdownRef = useRef(null);

  // Use external refs if provided, otherwise use internal refs
  const containerRefToUse = containerRef || internalContainerRef;
  const inputRefToUse = inputRef || internalInputRef;

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRefToUse.current &&
        !containerRefToUse.current.contains(event.target)
      ) {
        onVisibilityChange(false);
        setSelectedIndex(-1);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onVisibilityChange, containerRefToUse]);

  // Selecting a game (via mouse click or Enter key) is funneled through this
  // single handler so the "game_search_select" analytics event is emitted
  // exactly once regardless of input method, for every consumer of this
  // shared component (Explore, AddGameModal, SessionForm, ScorePad, etc.).
  const handleGameSelect = (game) => {
    trackClick('game_search_select', { query: searchQuery, game_id: game.id, game_name: game.name });
    onGameSelect(game);
    onVisibilityChange(false);
    setSelectedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isVisible || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          handleGameSelect(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onVisibilityChange(false);
        setSelectedIndex(-1);
        inputRefToUse.current?.blur();
        break;
      default:
        // Handle any other key cases
        break;
    }
  };

  // Handle input focus/blur
  const handleInputFocus = () => {
    setIsFocused(true);
    if (searchQuery.trim()) {
      onVisibilityChange(true);
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    // Delay hiding dropdown to allow click events on results
    setTimeout(() => {
      if (!isFocused) {
        onVisibilityChange(false);
        setSelectedIndex(-1);
      }
    }, 150);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    onSearchChange(value);
    setSelectedIndex(-1);

    if (value.trim()) {
      onVisibilityChange(true);
    } else {
      onVisibilityChange(false);
    }
  };

  const getImageUrl = (game) => {
    if (showImages) {
      return game.thumbnail || game.image || "/placeholder-game.png";
    }
    return null;
  };

  const renderGameInfo = (game) => {
    const imageUrl = getImageUrl(game);

    return (
      <>
        {imageUrl && (
          <img
            src={imageUrl}
            alt={game.name}
            className="game-result-image"
          />
        )}
        <div className="game-result-info">
          <div className="game-result-name">
            {game.name}
          </div>
          <div className="game-result-meta">
            {game.year_published && (
              <span>{game.year_published}</span>
            )}
            {game.year_published && game.min_players && game.max_players && (
              <span> • </span>
            )}
            {game.min_players && game.max_players && (
              <span>{game.min_players}-{game.max_players} players</span>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <div
      ref={containerRefToUse}
      className={`game-search-container${trailingSlot ? " game-search-container--with-trailing" : ""}`}
      style={{
        position: "relative",
        ...containerStyle
      }}
    >
      <input
        ref={inputRefToUse}
        type="text"
        placeholder={placeholder}
        value={searchQuery}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        autoFocus={autoFocus}
        className="game-search-input"
        style={inputStyle}
      />

      {trailingSlot ? (
        <div className="game-search-trailing">
          {trailingSlot}
        </div>
      ) : null}

      {isVisible && (
        <div
          ref={internalDropdownRef}
          className="game-search-dropdown"
          style={dropdownStyle}
        >
          {isLoading ? (
            <div className="game-search-loading">
              {loadingMessage}
            </div>
          ) : searchResults.length > 0 ? (
            searchResults.map((game, index) => (
              <div
                key={game.id}
                className={`game-search-result ${index === selectedIndex ? 'selected' : ''}`}
                data-game-id={game.id}
                data-game-name={game.name}
                data-track-handled="true"
                onClick={() => handleGameSelect(game)}
                onMouseEnter={() => setSelectedIndex(index)}
                onMouseLeave={() => setSelectedIndex(-1)}
              >
                {renderGameInfo(game)}
              </div>
            ))
          ) : showNoResultsMessage ? (
            <div className="game-search-no-results">
              {noResultsMessage}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default GameSearchDropdown;
